import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Modal, Form, Select, Badge, Space, Descriptions } from 'antd';
import { PlusOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, EyeOutlined, SwapOutlined, CopyOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const MigrateUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Use the getApiEndpoint function to get the correct URL
      const endpoint = getApiEndpoint('/migration/users');
      console.log('[MigrateUsersPage] Fetching users from endpoint:', endpoint);
      const response = await authFetch(endpoint);
      console.log('[MigrateUsersPage] Response status:', response.status);
      if (response.ok) {
        const responseData = await response.json();
        console.log('[MigrateUsersPage] Response data:', responseData);
        console.log('[MigrateUsersPage] Users array:', responseData.data);
        console.log('[MigrateUsersPage] Number of users:', responseData.data?.length);
        setUsers(responseData.data || []);
      } else {
        console.error('[MigrateUsersPage] Failed to fetch users, status:', response.status);
        toast.error('Failed to fetch migration users');
      }
    } catch (error) {
      console.error('[MigrateUsersPage] Error fetching users:', error);
      toast.error('Failed to fetch migration users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserById = async (id) => {
    try {
      const endpoint = getApiEndpoint(`/migration/users/${id}`);
      const response = await authFetch(endpoint);
      if (response.ok) {
        const user = await response.json();
        return user;
      }
      throw new Error('Failed to fetch user');
    } catch (error) {
      toast.error('Failed to fetch user details');
      return null;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      // Direct API Gateway URL for migration endpoints
      const endpoint = getApiEndpoint('/migration/users');
      console.log('[MigrateUsersPage] Creating user at endpoint:', endpoint);
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        toast.success('Migration user created successfully');
        setShowCreateDialog(false);
        form.resetFields();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create migration user');
    }
    setLoading(false);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    const values = await editForm.validateFields();
    setLoading(true);
    try {
      const endpoint = getApiEndpoint(`/migration/users/${editingUser.id}`);
      const response = await authFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingUser, ...values }),
      });

      if (response.ok) {
        toast.success('User updated successfully');
        setShowEditDialog(false);
        setEditingUser(null);
        editForm.resetFields();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
    setLoading(false);
  };

  const handleDeleteUser = async (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this user?',
      onOk: async () => {
        try {
          const endpoint = getApiEndpoint(`/migration/users/${id}`);
          const response = await authFetch(endpoint, {
            method: 'DELETE',
          });

          if (response.ok) {
            toast.success('User deleted successfully');
            fetchUsers();
          } else {
            toast.error('Failed to delete user');
          }
        } catch (error) {
          toast.error('Failed to delete user');
        }
      },
    });
  };

  const handleStartTemplateMigration = async () => {
    setLoading(true);
    try {
      const endpoint = getApiEndpoint('/migration/templates/migrate');
      const response = await authFetch(endpoint, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Template migration started');
        setTimeout(fetchUsers, 2000);
      } else {
        toast.error('Failed to start template migration');
      }
    } catch (error) {
      toast.error('Failed to start template migration');
    }
    setLoading(false);
  };

  const handleStartBalanceMigration = async () => {
    setLoading(true);
    try {
      const endpoint = getApiEndpoint('/migration/balance/migrate');
      const response = await authFetch(endpoint, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Balance migration started');
        setTimeout(fetchUsers, 2000);
      } else {
        toast.error('Failed to start balance migration');
      }
    } catch (error) {
      toast.error('Failed to start balance migration');
    }
    setLoading(false);
  };

  const handlePauseMigration = async (username) => {
    try {
      const endpoint = getApiEndpoint(`/migration/templates/pause/${username}`);
      const response = await authFetch(endpoint, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Migration paused');
        fetchUsers();
      } else {
        toast.error('Failed to pause migration');
      }
    } catch (error) {
      toast.error('Failed to pause migration');
    }
  };

  const handleResumeMigration = async (username) => {
    try {
      const endpoint = getApiEndpoint(`/migration/templates/resume/${username}`);
      const response = await authFetch(endpoint, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Migration resumed');
        fetchUsers();
      } else {
        toast.error('Failed to resume migration');
      }
    } catch (error) {
      toast.error('Failed to resume migration');
    }
  };

  const handleStopMigration = async (username) => {
    try {
      const endpoint = getApiEndpoint(`/migration/templates/stop/${username}`);
      const response = await authFetch(endpoint, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('Migration stopped');
        fetchUsers();
      } else {
        toast.error('Failed to stop migration');
      }
    } catch (error) {
      toast.error('Failed to stop migration');
    }
  };

  const handleMigrateUser = async (record) => {
    Modal.confirm({
      title: `Migrate user "${record.username}" to live system?`,
      content: 'This will copy the user to way2connect.users with is_active=0. The user will need to be activated manually.',
      okText: 'Migrate',
      okType: 'primary',
      onOk: async () => {
        try {
          const endpoint = getApiEndpoint('/migration/users/migrate-to-live');
          const response = await authFetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ migration_user_id: record.id }),
          });
          const result = await response.json();
          if (response.ok) {
            toast.success(result.message || 'User migrated successfully');
            fetchUsers();
          } else {
            toast.error(result.error || 'Failed to migrate user');
            fetchUsers();
          }
        } catch (error) {
          toast.error('Failed to migrate user');
        }
      },
    });
  };

  const handleMigrateTemplates = async (record) => {
    Modal.confirm({
      title: `Migrate templates for user "${record.username}" to live system?`,
      content: 'This will copy all templates, create entities/headers if needed, and set up template-header mappings in the live system.',
      okText: 'Migrate Templates',
      okType: 'primary',
      onOk: async () => {
        try {
          const endpoint = getApiEndpoint('/migration/templates/migrate-to-live');
          const response = await authFetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ migration_user_id: record.id }),
          });
          const result = await response.json();
          if (response.ok) {
            toast.success(`${result.message} (Migrated: ${result.migrated}, Skipped: ${result.skipped}, Errors: ${result.errors})`);
            fetchUsers();
          } else {
            toast.error(result.error || 'Failed to migrate templates');
            fetchUsers();
          }
        } catch (error) {
          toast.error('Failed to migrate templates');
        }
      },
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: 'default',
      RUNNING: 'processing',
      PAUSED: 'warning',
      DONE: 'success',
      ERROR: 'error',
      STOPPED: 'error',
      ALREADY_EXISTS: 'warning',
    };
    return <Badge status={statusConfig[status] || 'default'} text={status || 'PENDING'} />;
  };

  const columns = [
    {
      title: 'No',
      key: 'no',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (apiKey) => {
        if (!apiKey) return '-';
        return `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 4)}`;
      }
    },
    {
      title: 'User Sync Status',
      dataIndex: 'user_sync_status',
      key: 'user_sync_status',
      render: (status) => getStatusBadge(status),
    },
    {
      title: 'Template Sync Status',
      dataIndex: 'template_sync_status',
      key: 'template_sync_status',
      render: (status, record) => (
        <Space>
          {getStatusBadge(status)}
          {status === 'RUNNING' && (
            <Button size="small" onClick={() => handlePauseMigration(record.username)}>
              <PauseCircleOutlined />
            </Button>
          )}
          {status === 'PAUSED' && (
            <Button size="small" onClick={() => handleResumeMigration(record.username)}>
              <PlayCircleOutlined />
            </Button>
          )}
          {(status === 'RUNNING' || status === 'PAUSED') && (
            <Button size="small" danger onClick={() => handleStopMigration(record.username)}>
              <StopOutlined />
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Balance Sync Status',
      dataIndex: 'balance_sync_status',
      key: 'balance_sync_status',
      render: (status) => getStatusBadge(status),
    },
    {
      title: 'User Migrate Status',
      dataIndex: 'user_migrate_status',
      key: 'user_migrate_status',
      render: (status, record) => (
        <Space>
          {getStatusBadge(status)}
          {(!status || status === 'PENDING' || status === 'ERROR') && (
            <Button
              size="small"
              type="primary"
              icon={<SwapOutlined />}
              onClick={() => handleMigrateUser(record)}
            >
              Migrate
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Template Migrate Status',
      dataIndex: 'template_migrate_status',
      key: 'template_migrate_status',
      render: (status, record) => (
        <Space>
          {getStatusBadge(status)}
          {(!status || status === 'PENDING' || status === 'ERROR') && (
            <Button
              size="small"
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => handleMigrateTemplates(record)}
            >
              Migrate Templates
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => (balance !== null && balance !== undefined) ? Number(balance).toFixed(2) : '0.00',
    },
    {
      title: 'Last Synced',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      render: (date) => (date ? new Date(date).toLocaleString() : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={async () => {
              const fullUser = await fetchUserById(record.id);
              if (fullUser) {
                setViewingUser(fullUser);
                setShowViewDialog(true);
              }
            }}
          >
            <EyeOutlined />
          </Button>
          <Button
            size="small"
            onClick={() => {
              setEditingUser(record);
              editForm.setFieldsValue(record);
              setShowEditDialog(true);
            }}
          >
            <EditOutlined />
          </Button>
          <Button size="small" danger onClick={() => handleDeleteUser(record.id)}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold">Migrate Users</h1>
        <Space>
          <Button onClick={handleStartTemplateMigration} loading={loading}>
            <PlayCircleOutlined />
            Start Template Migration
          </Button>
          <Button onClick={handleStartBalanceMigration} loading={loading}>
            <PlayCircleOutlined />
            Start Balance Migration
          </Button>
          <Button onClick={fetchUsers} icon={<ReloadOutlined />}>
            Refresh
          </Button>
        </Space>
      </div>

      <Card
        title="Migration Users"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreateDialog(true)}>
            Create Migration User
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={Array.isArray(users) ? users : []}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Create Migration User"
        open={showCreateDialog}
        onOk={handleCreateUser}
        onCancel={() => {
          setShowCreateDialog(false);
          form.resetFields();
        }}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>
          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter API key" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Migration User"
        open={showEditDialog}
        onOk={handleUpdateUser}
        onCancel={() => {
          setShowEditDialog(false);
          setEditingUser(null);
          editForm.resetFields();
        }}
        confirmLoading={loading}
      >
        {editingUser && (
          <Form form={editForm} layout="vertical" initialValues={editingUser}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="api_key"
              label="API Key"
              rules={[{ required: true, message: 'Please enter API key' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="is_active" label="Active">
              <Select>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="View Migration User"
        open={showViewDialog}
        onCancel={() => {
          setShowViewDialog(false);
          setViewingUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowViewDialog(false);
            setViewingUser(null);
          }}>
            Close
          </Button>
        ]}
        width={600}
      >
        {viewingUser && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Username">
              {viewingUser.username}
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              {viewingUser.id}
            </Descriptions.Item>
            <Descriptions.Item label="API Key">
              <Input.Password
                value={viewingUser.api_key}
                readOnly
                visibilityToggle
              />
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge 
                status={viewingUser.is_active ? 'success' : 'error'} 
                text={viewingUser.is_active ? 'Active' : 'Inactive'} 
              />
            </Descriptions.Item>
            <Descriptions.Item label="User Sync Status">
              {getStatusBadge(viewingUser.user_sync_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Template Sync Status">
              {getStatusBadge(viewingUser.template_sync_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Balance Sync Status">
              {getStatusBadge(viewingUser.balance_sync_status)}
            </Descriptions.Item>
            <Descriptions.Item label="User Migrate Status">
              {getStatusBadge(viewingUser.user_migrate_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Template Migrate Status">
              {getStatusBadge(viewingUser.template_migrate_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Balance">
              {viewingUser.balance ? viewingUser.balance.toFixed(2) : '0.00'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {new Date(viewingUser.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Last Synced">
              {viewingUser.last_synced_at ? new Date(viewingUser.last_synced_at).toLocaleString() : 'Never'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MigrateUsersPage;
